import React, { useContext, useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel, openPanel } from '@state/actions/panelsActions'
import { useSchoolClasses } from '@hooks/useSchoolClasses'
import { useUpdateSchoolClass } from '@hooks/mutations/useUpdateSchoolClass'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Button from '@components/Button'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Loader from '@components/Loader'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useDeleteCustomer } from '@hooks/mutations/useDeleteCustomer'
import { useConfirm } from '@hooks/useConfirm'
import { useSortAndLabelStudentList } from '@hooks/useSortAndLabelStudentList'

const ManageStudentsPanel = () => {
  const [, dispatch] = useContext(AppContext)

  const { currentUser, error, isLoading } = useCurrentUser()
  const [allStudents, setAllStudents] = useState(null)
  const { studentOptions } = useSortAndLabelStudentList(allStudents)

  useEffect(() => {
    if (currentUser?.schools && !error) {
      setAllStudents(currentUser.schools.flatMap((school) => school.students))
    }
  }, [currentUser, error])

  const { confirm } = useConfirm()
  const { post, isPosting } = useDeleteCustomer()
  const { register, handleSubmit, errors, control, watch, setValue } = useForm()
  const watchStudent = watch('student', false)

  const onSubmit = async (data) => {
    const {
      student: { item: student },
    } = data
    const confirmed = await confirm({
      title: `${student.first_name} ${student.last_name} (${student.email})`,
      text: 'Är du säker på att du vill radera den här eleven?',
      confirmText: 'Ja, radera eleven',
      cancelText: 'Avbryt',
    })

    if (confirmed) {
      const res = await post(
        { id: student.id },
        'Eleven är nu raderad.',
        'Någonting gick fel, var god försök igen.'
      )

      if (res.success) {
        setValue('student', null)
      }
    }
  }

  return (
    <>
      <div className={'relative'}>
        <div className='absolute top-0 right-0'>
          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.MANAGE_STUDENTS))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          ></Button>
        </div>
      </div>

      {/* {schoolClasses && ( */}
      <div className={'flex flex-col items-center'}>
        <Divider size={DIVIDER_SIZE.SMALL} />

        {isLoading && <Loader />}
        {error && <Text>Någonting gick fel när vi skulle hämta elever</Text>}
        {!error && !isLoading && studentOptions?.length && (
          <>
            <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
              Hantera elever
            </Heading>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <Form
              onSubmit={handleSubmit(onSubmit)}
              className={'w-full max-w-md flex flex-col items-center'}
            >
              <Form.Label htmlFor='student'>Elever:</Form.Label>
              <Controller
                control={control}
                name={'student'}
                defaultValue={null}
                rules={{
                  validate: (value) => {
                    return !!value
                  },
                }}
                render={({ onChange, name, value }) => (
                  <Form.MultiSelect
                    name={name}
                    value={value}
                    onChange={(option) => onChange(option)}
                    options={studentOptions}
                    placeholder={'Sök elev'}
                    isSearchable
                    hideSelectedOptions={false}
                  />
                )}
              />
              {errors.student && (
                <Form.ErrorMessage>Välj en elev</Form.ErrorMessage>
              )}

              <Divider size={DIVIDER_SIZE.SMALL} />

              <ButtonWithLoader
                type={BUTTON_TYPE.PRIMARY}
                isLoading={isPosting}
                disabledText={'Radera elev'}
                enabledText={'Radera elev'}
                disabled={isPosting || !watchStudent}
              />
            </Form>
          </>
        )}
      </div>
      {/* )} */}
    </>
  )
}

export default ManageStudentsPanel
